import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Leaf, Loader2, Copy, Check, Mail } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";

// Admin: paste "Name, email" lines → creates retreat accounts + emails each a login.
const AddRetreatGuests = ({ headers, onDone }) => {
  const [open, setOpen] = useState(false);
  const [retreats, setRetreats] = useState([]);
  const [retreatId, setRetreatId] = useState("");
  const [text, setText] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/retreats`, { headers }).then((r) => {
      setRetreats(r.data || []);
      if (r.data?.[0] && !retreatId) setRetreatId(r.data[0].id);
    }).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const parse = () =>
    text.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const m = line.match(/([^\s,;<>]+@[^\s,;<>]+)/);
      if (!m) return null;
      const email = m[1];
      const name = line.replace(email, "").replace(/[,;<>]/g, " ").trim();
      return { name: name || email.split("@")[0], email };
    }).filter(Boolean);

  const submit = async () => {
    const guests = parse();
    if (!retreatId || guests.length === 0) { toast.error("Paste at least one 'Name, email' line"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/retreats/${retreatId}/guests`, { guests, send_email: sendEmail }, { headers });
      setResults(res.data.results);
      const created = res.data.results.filter((r) => r.status === "created").length;
      toast.success(`${created} retreat account${created === 1 ? "" : "s"} created`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create retreat guests");
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    const lines = (results || []).filter((r) => r.password).map((r) => `${r.name} — ${r.email} — ${r.password}`).join("\n");
    await navigator.clipboard.writeText(`Villa Savi app · savicoliving.com/login\n${lines}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => { setResults(null); setText(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full border-[#8C9A7C] text-[#5C6A4C] hover:bg-[#8C9A7C]/10" data-testid="add-retreat-guests-btn">
          <Leaf className="w-4 h-4 mr-1.5" /> Retreat group
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#3D4035]">Add a retreat group</DialogTitle>
        </DialogHeader>
        {!results ? (
          <div className="space-y-4 pt-1">
            <select value={retreatId} onChange={(e) => setRetreatId(e.target.value)} className="w-full h-11 rounded-xl border border-stone-200 px-3 text-sm bg-white" data-testid="retreat-select">
              {retreats.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.start.slice(5)} → {r.end.slice(5)} · {r.guest_count} in app</option>)}
            </select>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={"One per line, name then email:\nRamona Hechenberger, ramona@example.com\nEmily emily@example.com"}
              className="w-full rounded-xl border border-stone-200 px-3 py-2.5 text-sm font-mono"
              data-testid="retreat-guests-textarea"
            />
            <label className="flex items-center gap-2 text-sm text-[#3D4035]/80">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="retreat-send-email-toggle" />
              <Mail className="w-4 h-4 text-[#5C6A4C]" /> Email each person their login (German + English)
            </label>
            <p className="text-xs text-[#3D4035]/50">
              They get a short 3-screen setup (photo, dietary, fun fact) instead of the full coliving wizard, and House Points / AI matches are hidden for them. {parse().length > 0 && <span className="text-[#5C6A4C] font-medium">{parse().length} people detected.</span>}
            </p>
            <Button onClick={submit} disabled={busy} className="w-full bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white rounded-full" data-testid="submit-retreat-guests-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Leaf className="w-4 h-4 mr-2" />} Create accounts{sendEmail ? " & send emails" : ""}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 pt-1" data-testid="retreat-results">
            <div className="max-h-72 overflow-y-auto divide-y divide-stone-100 border border-stone-200 rounded-xl">
              {results.map((r) => (
                <div key={r.email} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`retreat-result-${r.status}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-[#3D4035] truncate">{r.name}</p>
                    <p className="text-xs text-[#3D4035]/50 truncate">{r.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {r.password ? <p className="font-mono text-xs text-[#3D4035]">{r.password}</p> : <p className="text-xs text-[#3D4035]/50">already had an account</p>}
                    <p className={`text-[10px] ${r.emailed ? "text-[#5C6A4C]" : "text-[#3D4035]/40"}`}>{r.status === "created" ? (r.emailed ? "emailed ✓" : "not emailed") : "marked as retreat"}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyAll} className="flex-1 rounded-full" data-testid="copy-retreat-logins-btn">
                {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />} Copy logins for WhatsApp
              </Button>
              <Button onClick={() => setOpen(false)} className="rounded-full bg-[#3D4035] text-white">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddRetreatGuests;
